import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { useCreateTagCategory, useUpdateTagCategory } from "@/services/query/tag-categories-queries";
import { TagCategory } from "@/services/supabase/tag-categories";
import { Loader2 } from "lucide-react";
import React, { useEffect, useState } from "react";

interface TagCategoryDialogProps {
    isOpen: boolean;
    onClose: () => void;
    mode: 'create' | 'edit';
    category?: TagCategory;
    projectId: number;
}

export function TagCategoryDialog({
    isOpen,
    onClose,
    mode,
    category,
    projectId
}: TagCategoryDialogProps) {
    const { toast } = useToast();
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    const createMutation = useCreateTagCategory();
    const updateMutation = useUpdateTagCategory();

    const isSubmitting = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (isOpen) {
            if (mode === 'edit' && category) {
                setName(category.name);
                setDescription(category.description || "");
            } else {
                setName("");
                setDescription("");
            }
        }
    }, [isOpen, mode, category]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim()) {
            toast({
                title: "Validation Error",
                description: "Category name is required.",
                variant: "destructive",
            });
            return;
        }

        try {
            if (mode === 'create') {
                await createMutation.mutateAsync({
                    name,
                    description: description || undefined,
                    project_id: projectId,
                });
                toast({
                    title: "Category Created",
                    description: `Category "${name}" created successfully.`,
                });
            } else if (mode === 'edit' && category) {
                await updateMutation.mutateAsync({
                    categoryId: category.id,
                    updates: {
                        name,
                        description: description || undefined,
                    },
                    projectId: projectId,
                });
                toast({
                    title: "Category Updated",
                    description: `Category "${name}" updated successfully.`,
                });
            }
            onClose();
        } catch (error) {
            console.error(error);
            toast({
                title: "Error",
                description: `Failed to ${mode} category. Please try again.`,
                variant: "destructive",
            });
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="sm:max-w-[500px] border-border/50 shadow-2xl backdrop-blur-xl bg-background/95 p-0 overflow-hidden gap-0">
                <DialogHeader className="px-6 pt-6 pb-4 border-b border-border/50">
                    <DialogTitle className="text-xl font-bold tracking-tight">
                        {mode === 'create' ? 'Create Category' : 'Edit Category'}
                    </DialogTitle>
                </DialogHeader>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-6 px-6 py-6">
                        <div className="space-y-2">
                            <Label htmlFor="name" className="text-sm font-medium text-foreground">
                                Name
                            </Label>
                            <Input
                                id="name"
                                placeholder="e.g. Themes, Arcs"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="bg-muted/30 border-border/50 focus:bg-background focus:border-primary/20 transition-all shadow-sm"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="description" className="text-sm font-medium text-foreground">
                                Description (Optional)
                            </Label>
                            <Textarea
                                id="description"
                                placeholder="Describe what this category is for..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                className="min-h-[100px] bg-muted/30 border-border/50 focus:bg-background focus:border-primary/20 transition-all resize-none shadow-sm"
                            />
                        </div>
                    </div>

                    <div className="px-6 py-4 bg-muted/20 border-t border-border/50 flex justify-end gap-3">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={onClose}
                            disabled={isSubmitting}
                            className="hover:bg-muted/50"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={isSubmitting}
                            className={cn(
                                "shadow-lg shadow-primary/20 transition-all hover:shadow-primary/40",
                                isSubmitting && "opacity-80"
                            )}
                        >
                            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {mode === 'create' ? 'Create Category' : 'Save Changes'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
